import { useSearchParams } from 'react-router-dom';

import Button from '@/components/common/Button';

import { ReactComponent as TriangleUp } from '@/assets/icons/triangle-up.svg';
import { ReactComponent as TriangleDown } from '@/assets/icons/triangle-down.svg';
import { ReactComponent as ArrowLeft } from '@/assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRight } from '@/assets/icons/arrow-right.svg';

import { COLOR, DIRECTION, SORT_COLOR_MAPPING } from './constant';

const renderHeaders = ({
	headers,
	handleSortMeta,
	sortMeta,
}: RenderHeaderParam) => (
	<thead className="bg-grey-subtle">
		<tr className="border border-grey-soft">
			{headers.map((h) => (
				<th key={h.key} className={`px-6 py-4 ${h.classNames || ''} `}>
					<div className="flex items-center">
						<span className="mr-2"> {h.label.toUpperCase()}</span>
						<div>
							<TriangleUp
								fill={
									sortMeta.sortBy !== h.key
										? COLOR.ACTIVE
										: SORT_COLOR_MAPPING[sortMeta.order].UP
								}
								stroke="black"
								onClick={() => handleSortMeta(h.key, DIRECTION.ASC)}
							/>
							<TriangleDown
								fill={
									sortMeta.sortBy !== h.key
										? COLOR.ACTIVE
										: SORT_COLOR_MAPPING[sortMeta.order].DOWN
								}
								stroke="black"
								onClick={() => handleSortMeta(h.key, DIRECTION.DESC)}
							/>
						</div>
					</div>
				</th>
			))}
		</tr>
	</thead>
);

const renderBodies = (
	data: TableBodyData[],
	keys: string[],
	component?: any
) => {
	const renderValue = (d: TableBodyData, key: string) => {
		if (component && component[key]) {
			if (d.value[key]) {
				return component[key](d.value[key]);
			}
			return component[key]('');
		}
		return d.value[key];
	};
	return (
		<tbody>
			{data.map((d: TableBodyData) => (
				<tr key={d.id} className="border border-grey-soft">
					{keys.map((k) => (
						<td key={k} className=" px-6 py-4 align-top">
							{renderValue(d, k)}
						</td>
					))}
				</tr>
			))}
		</tbody>
	);
};

const renderPaginationFooter = ({
	paginationMeta,
	handlePageNumberChange,
}: RenderFooterParam) => {
	const { startItem, endItem, totalItem, pageNumber } = paginationMeta;
	const itemOffsetInformation = `	${startItem}-${endItem}`;

	const isDisabledPreviousButton = pageNumber === 0;
	const isDisabledNextButton = pageNumber === paginationMeta.lastPage;

	return (
		<div className="flex justify-between px-6 py-4 border border-t-0 border-grey-soft">
			<div className="flex items-center">
				<span className="mr-1">Showing</span>
				<span>
					<b>{itemOffsetInformation}</b> of <b>{`${totalItem}`}</b>
				</span>
			</div>
			<div className="flex">
				<Button
					variant="basic"
					disabled={isDisabledPreviousButton}
					onClick={() => handlePageNumberChange(pageNumber - 1)}
				>
					<ArrowLeft />
					<span className="ml-2">Previous</span>
				</Button>
				<Button
					variant="basic"
					disabled={isDisabledNextButton}
					onClick={() => handlePageNumberChange(pageNumber + 1)}
				>
					<span className="mr-2">Next</span>
					<ArrowRight />
				</Button>
			</div>
		</div>
	);
};

const Table: React.FC<TableProps> = (props) => {
	const {
		headers,
		body,
		meta,
		sort,
		renderMeta = true,
		pageNumberHandler,
	} = props;
	const { data, component } = body;

	const tableKeys = headers.map((h) => h.key);
	const [searchParams, setSearchParams] = useSearchParams();

	const tableMeta: TableMeta = {
		sortBy: searchParams.get('sortBy'),
		order: (searchParams.get('order') || DIRECTION.DESC) as Direction,
		pageNumber: Number(searchParams.get('pageNumber')),
	};

	meta.pageNumber = tableMeta.pageNumber;

	const handleSortClient = () => {
		const { sortBy, order } = tableMeta;
		if (!sortBy) {
			const firstKey = tableKeys.find((k) => k);

			return data.sort((a, b) => {
				if (sort.customSort[firstKey]) {
					return sort.customSort[firstKey](
						b.value[firstKey],
						a.value[firstKey],
						'ASC'
					);
				}
				return a.value[firstKey].localeCompare(b.value[firstKey]);
			});
		}
		if (data.length === 0) {
			return [];
		}
		if (order === 'DESC') {
			return data.sort((a, b) => {
				if (sort.customSort[sortBy]) {
					return sort.customSort[sortBy](
						b.value[sortBy],
						a.value[sortBy],
						'DESC'
					);
				}
				return b.value[sortBy].toString().localeCompare(a.value[sortBy]);
			});
		}
		return data.sort((a, b) => {
			if (sort.customSort) {
				return sort.customSort[sortBy](b.value[sortBy], a.value[sortBy], 'ASC');
			}
			return a.value[sortBy].toString().localeCompare(b.value[sortBy]);
		});
	};

	const usedData = sort.type === 'CLIENT' ? handleSortClient() : data;

	const handleSortMeta = (sortBy: string, order: Direction) => {
		setSearchParams( prev => (
			new URLSearchParams({
				...Object.fromEntries(prev.entries()),
				pageNumber: String(tableMeta.pageNumber),
				sortBy,
				order,
			})
		));
	};

	const handlePageNumberChange = (pageNumber: number) => {
		if (pageNumberHandler) {
			pageNumberHandler(pageNumber);
			return;
		}
		setSearchParams(prev => (
			new URLSearchParams({
				...Object.fromEntries(prev.entries()),
				...tableMeta,
				pageNumber: String(pageNumber),
			})
		));
	};

	return (
		<div className="flex flex-col w-full">
			<table className="table-auto w-full text-sm text-left">
				{renderHeaders({ headers, handleSortMeta, sortMeta: tableMeta })}
				{renderBodies(usedData, tableKeys, component)}
			</table>
			{renderMeta &&
				renderPaginationFooter({
					paginationMeta: meta,
					handlePageNumberChange,
				})}
		</div>
	);
};

export default Table;
